import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { useAlert } from 'dashboard/composables';

export const voiceRecorderState = () => ({
  audioSession: 0,
  audioPreparing: false,
  audioSendRequested: false,
  audioSendInFlight: false,
});

export const voiceRecorderMethods = {
  onAudioReady() {
    if (this.isRecordingAudio) this.recordingAudioState = 'recording';
  },
  onAudioProcessing() {
    if (!this.isRecordingAudio) return;
    this.audioPreparing = true;
    this.recordingAudioState = 'processing';
  },
  async onSendReply() {
    if (!this.isRecordingAudio) return this.sendPreparedReply();
    if (this.isEditorDisabled || this.audioPreparing || this.audioSendInFlight) return;
    if (!this.hasRecordedAudio) {
      if (this.recordingAudioState !== 'recording') return;
      this.audioSendRequested = true;
      this.audioSendInFlight = true;
      this.$refs.audioRecorderInput.stopRecording();
      return;
    }
    this.audioSendInFlight = true;
    try {
      await this.sendPreparedReply();
    } finally {
      this.audioSendInFlight = false;
    }
  },
  async onFinishRecorder(file) {
    if (!this.isRecordingAudio) return;
    const session = this.audioSession;
    const conversationId = this.currentChat.id;
    const current = () => this.isRecordingAudio && this.audioSession === session &&
      this.currentChat.id === conversationId && !this.isOnPrivateNote;
    this.audioPreparing = true;
    this.recordingAudioState = 'processing';
    try {
      if (!file?.file?.size) throw new Error('empty_recording');
      const maxSize = this.maxSizeFor(file.type);
      if (!checkFileSizeLimit(file, maxSize)) {
        this.alertOverLimit(maxSize);
        this.resetAudioRecorderInput();
        return;
      }
      const voiceFile = { ...file, isVoiceMessage: true };
      let blob;
      if (this.globalConfig.directUploadsEnabled) {
        blob = await new Promise((resolve, reject) => {
          const upload = new DirectUpload(file.file,
            `/api/v1/accounts/${this.accountId}/conversations/${conversationId}/direct_uploads`, {
              directUploadWillCreateBlobWithXHR: setDirectUploadAuthHeaders,
            });
          upload.create((error, result) => error ? reject(new Error('voice_upload_failed')) : resolve(result));
        });
      }
      if (!current()) return;
      // Same attachment contract and authenticated upload path as fileUploadMixin.
      this.attachedFiles.push({ currentChatId: conversationId, resource: blob || voiceFile,
        isPrivate: this.isPrivate, thumb: '', blobSignedId: blob?.signed_id, isVoiceMessage: true });
      this.hasRecordedAudio = true;
      this.recordingAudioState = 'stopped';
      this.audioPreparing = false;
      if (this.audioSendRequested && !this.isEditorDisabled) {
        this.audioSendRequested = false;
        await this.sendPreparedReply();
      }
    } catch {
      if (current()) this.onRecordError();
    } finally {
      if (this.audioSession === session) {
        this.audioPreparing = false;
        this.audioSendInFlight = false;
      }
    }
  },
  onRecordError() {
    this.resetAudioRecorderInput();
    useAlert(this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
  },
};
